import React from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ChevronUp, ChevronDown, Eye, EyeOff, Lock, AlertTriangle, RotateCcw } from "lucide-react";
import { HOME_SECTIONS, homeSectionMeta, resolveHomeSections } from "../homeSections";

const ArrowBtn = ({ onClick, disabled, testId, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    aria-label={label}
    className="p-1.5 rounded text-white/60 hover:text-white hover:bg-white/[0.08] disabled:opacity-20 disabled:pointer-events-none transition-colors"
  >
    {children}
  </button>
);

const VisibilityToggle = ({ s, meta, toggle }) =>
  meta.locked ? (
    <span data-testid={`home-section-locked-${s.id}`} title="Toujours affichée" className="flex items-center justify-center gap-1 px-2 py-1 sm:w-36 text-[11px] text-white/40">
      <Lock size={12} /> <span className="hidden sm:inline">Toujours affichée</span>
    </span>
  ) : (
    <button
      type="button"
      onClick={() => toggle(s.id)}
      data-testid={`home-section-toggle-${s.id}`}
      title={s.visible ? "Visible" : "Masquée"}
      className={`flex items-center justify-center gap-1 px-2 py-1 sm:w-36 rounded text-[11px] font-semibold transition-colors ${
        s.visible ? "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" : "bg-white/[0.06] text-white/50 hover:bg-white/[0.1]"
      }`}
    >
      {s.visible ? <Eye size={12} /> : <EyeOff size={12} />}
      <span className="hidden sm:inline">{s.visible ? "Visible" : "Masquée"}</span>
    </button>
  );

const SectionRow = ({ s, idx, count, move, toggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  const meta = homeSectionMeta(s.id);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : "auto" }}
      {...attributes}
      data-testid={`home-section-row-${s.id}`}
      className={`relative flex items-center gap-2 border rounded-md px-2 py-2 ${
        s.visible ? "border-white/[0.06] bg-[#1C1C1E]" : "border-white/[0.04] bg-[#1C1C1E]/50"
      } ${isDragging ? "ring-2 ring-[#FF3B30] shadow-2xl" : ""}`}
    >
      <button
        type="button"
        {...listeners}
        data-testid={`home-section-drag-${s.id}`}
        aria-label="Glisser pour réordonner"
        className="cursor-grab active:cursor-grabbing touch-none text-white/40 hover:text-white p-1 rounded hover:bg-white/[0.06]"
      >
        <GripVertical size={16} />
      </button>
      <span className="text-white/30 text-xs w-5 text-right">{idx + 1}.</span>
      <div className={`flex-1 min-w-0 ${s.visible ? "" : "opacity-50"}`}>
        <p className="text-white text-sm leading-snug break-words">{meta.label}</p>
        {meta.warning && (
          <p className="text-yellow-400/80 text-[11px] flex items-center gap-1" data-testid={`home-section-warning-${s.id}`}>
            <AlertTriangle size={11} className="flex-shrink-0" /> {meta.warning}
          </p>
        )}
      </div>
      <ArrowBtn onClick={() => move(idx, -1)} disabled={idx === 0} testId={`home-section-up-${s.id}`} label="Monter">
        <ChevronUp size={16} />
      </ArrowBtn>
      <ArrowBtn onClick={() => move(idx, 1)} disabled={idx === count - 1} testId={`home-section-down-${s.id}`} label="Descendre">
        <ChevronDown size={16} />
      </ArrowBtn>
      <VisibilityToggle s={s} meta={meta} toggle={toggle} />
    </div>
  );
};

export const HomeSectionsEditor = ({ data, setData }) => {
  const sections = resolveHomeSections(data.sections);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const save = (list) => setData({ ...data, sections: list });
  const move = (idx, dir) => save(arrayMove(sections, idx, idx + dir));
  const toggle = (id) => save(sections.map((s) => (s.id === id ? { ...s, visible: !s.visible } : s)));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    save(arrayMove(sections, sections.findIndex((s) => s.id === active.id), sections.findIndex((s) => s.id === over.id)));
  };

  return (
    <div className="space-y-3" data-testid="home-sections-editor">
      <p className="text-white/50 text-xs">
        Le bandeau principal reste toujours en haut et le pied de page toujours en bas. Glissez une ligne ou utilisez les flèches, puis cliquez sur « Enregistrer ».
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-1.5">
            {sections.map((s, idx) => (
              <SectionRow key={s.id} s={s} idx={idx} count={sections.length} move={move} toggle={toggle} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <button
        type="button"
        onClick={() => save(HOME_SECTIONS.map(({ id }) => ({ id, visible: true })))}
        data-testid="home-sections-reset"
        className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors"
      >
        <RotateCcw size={12} /> Ordre par défaut (tout afficher)
      </button>
    </div>
  );
};
